import {FC, useState, useEffect, useRef, useCallback} from 'react'
import {
  getTrainingRecordings,
  markAsPlayed,
  saveNotes,
  TrainingRecording,
} from './core/_training-requests'

function formatDuration(seconds: number): string {
  if (!seconds) return ''
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

const RecordingCard: FC<{recording: TrainingRecording; onPlayed: (id: string) => void}> = ({recording, onPlayed}) => {
  const [notes, setNotes] = useState(recording.notes || '')
  const [showNotes, setShowNotes] = useState(false)
  const [saving, setSaving] = useState(false)
  const [played, setPlayed] = useState(recording.played)
  const notesRef = useRef(recording.notes || '')

  const handlePlay = async () => {
    if (!played) {
      setPlayed(true)
      onPlayed(recording._id)
      try {
        await markAsPlayed(recording._id)
      } catch (e) {
        console.error('Error marking as played:', e)
      }
    }
  }

  const handleNotesSave = useCallback(async () => {
    if (notes === notesRef.current) return
    notesRef.current = notes
    setSaving(true)
    try {
      await saveNotes(recording._id, notes)
    } catch (e) {
      console.error('Error saving notes:', e)
    } finally {
      setSaving(false)
    }
  }, [notes, recording._id])

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: 20,
        marginBottom: 16,
        borderRight: played ? '4px solid #50cd89' : '4px solid #e4e6ef',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}
    >
      <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12}}>
        <div style={{display: 'flex', alignItems: 'center', gap: 10, flex: 1}}>
          {played && (
            <span style={{
              background: '#e8fff3',
              color: '#50cd89',
              padding: '3px 10px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}>
              <i className="bi bi-check-circle-fill" style={{marginLeft: 4}}></i>
              נשמע
            </span>
          )}
          <h4 style={{margin: 0, fontWeight: 700, fontSize: 16}}>{recording.title}</h4>
        </div>
        {recording.duration > 0 && (
          <span style={{
            background: '#f1faff',
            color: '#009ef7',
            padding: '3px 10px',
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 600,
          }}>
            {formatDuration(recording.duration)}
          </span>
        )}
      </div>

      {recording.description && (
        <p style={{color: '#a1a5b7', fontSize: 13, marginBottom: 12}}>{recording.description}</p>
      )}

      {recording.salesperson && (
        <div style={{marginBottom: 12}}>
          <span style={{color: '#a1a5b7', fontSize: 13}}>
            <i className="bi bi-person" style={{marginLeft: 4}}></i>
            {recording.salesperson}
          </span>
        </div>
      )}

      <div style={{display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12}}>
        {recording.tags?.map((tag, i) => (
          <span key={i} style={{
            background: '#f1f1f4',
            color: '#7239ea',
            padding: '2px 10px',
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 500,
          }}>{tag}</span>
        ))}
        {recording.rating != null && (
          <span style={{
            background: '#fff8dd',
            color: '#f1bc00',
            padding: '2px 10px',
            borderRadius: 20,
            fontSize: 11,
          }}>
            {'★'.repeat(recording.rating)}{'☆'.repeat(5 - recording.rating)}
          </span>
        )}
      </div>

      <audio
        controls
        onPlay={handlePlay}
        style={{width: '100%', height: 40}}
        preload='none'
      >
        <source src={recording.file_url} type='audio/mpeg' />
      </audio>

      <div style={{marginTop: 12}}>
        <button
          onClick={() => setShowNotes(!showNotes)}
          style={{
            background: showNotes ? '#e8f0fe' : '#f9f9f9',
            border: '1px solid #e4e6ef',
            borderRadius: 8,
            padding: '6px 14px',
            fontSize: 13,
            cursor: 'pointer',
            color: '#5e6278',
          }}
        >
          <i className={`bi ${showNotes ? 'bi-chevron-up' : 'bi-pencil'}`} style={{marginLeft: 6}}></i>
          הערות אישיות
          {notes && !showNotes && (
            <span style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#009ef7',
              marginRight: 6,
            }}></span>
          )}
        </button>

        {showNotes && (
          <div style={{marginTop: 8}}>
            <textarea
              style={{
                width: '100%',
                border: '1px solid #e4e6ef',
                borderRadius: 8,
                padding: 10,
                fontSize: 13,
                resize: 'vertical',
                minHeight: 70,
                direction: 'rtl',
              }}
              placeholder='כתוב הערות אישיות...'
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={handleNotesSave}
            />
            {saving && <small style={{color: '#a1a5b7'}}>שומר...</small>}
          </div>
        )}
      </div>
    </div>
  )
}

const SalesTrainingPage: FC = () => {
  const [recordings, setRecordings] = useState<TrainingRecording[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedTag, setSelectedTag] = useState('')

  useEffect(() => {
    loadRecordings()
  }, [])

  const loadRecordings = async () => {
    try {
      const data = await getTrainingRecordings()
      setRecordings(data.recordings || [])
    } catch (e) {
      console.error('Error loading recordings:', e)
    } finally {
      setLoading(false)
    }
  }

  const handlePlayed = (id: string) => {
    setRecordings(prev => prev.map(r => r._id === id ? {...r, played: true} : r))
  }

  const allTags = Array.from(new Set(recordings.flatMap(r => r.tags || [])))

  const filtered = recordings.filter(r => {
    const matchesSearch = !search ||
      r.title.toLowerCase().includes(search.toLowerCase()) ||
      r.description?.toLowerCase().includes(search.toLowerCase()) ||
      r.salesperson?.toLowerCase().includes(search.toLowerCase())
    const matchesTag = !selectedTag || r.tags?.includes(selectedTag)
    return matchesSearch && matchesTag
  })

  const playedCount = recordings.filter(r => r.played).length
  const totalCount = recordings.length
  const progressPercent = totalCount > 0 ? Math.round((playedCount / totalCount) * 100) : 0

  return (
    <div dir='rtl' style={{maxWidth: 800, margin: '0 auto'}}>
      <h2 style={{fontWeight: 700, marginBottom: 24}}>
        <i className="bi bi-headphones" style={{marginLeft: 8}}></i>
        הדרכות מכירה
      </h2>

      {/* Progress bar */}
      {totalCount > 0 && (
        <div style={{
          background: '#fff',
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}>
            <span style={{fontWeight: 600, color: '#3f4254'}}>התקדמות</span>
            <span style={{color: '#a1a5b7', fontSize: 13}}>
              {playedCount} מתוך {totalCount} הושלמו
            </span>
          </div>
          <div style={{
            background: '#f5f8fa',
            borderRadius: 10,
            height: 10,
            overflow: 'hidden',
          }}>
            <div style={{
              background: '#50cd89',
              height: '100%',
              width: `${progressPercent}%`,
              borderRadius: 10,
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>
      )}

      {/* Search & filter */}
      <div style={{display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap'}}>
        <input
          type='text'
          placeholder='חיפוש...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            minWidth: 200,
            border: '1px solid #e4e6ef',
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 13,
          }}
        />
        {allTags.length > 0 && (
          <select
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value)}
            style={{
              border: '1px solid #e4e6ef',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 13,
              minWidth: 150,
            }}
          >
            <option value=''>כל התגיות</option>
            {allTags.map(tag => (
              <option key={tag} value={tag}>{tag}</option>
            ))}
          </select>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{textAlign: 'center', padding: 60}}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">טוען...</span>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div style={{textAlign: 'center', padding: 60}}>
          <i className="bi bi-headphones" style={{fontSize: 48, color: '#d6d8e0', display: 'block', marginBottom: 12}}></i>
          <p style={{color: '#a1a5b7'}}>
            {search || selectedTag ? 'לא נמצאו הדרכות מתאימות' : 'אין הדרכות מפורסמות כרגע'}
          </p>
        </div>
      )}

      {/* Recording cards */}
      {filtered.map(recording => (
        <RecordingCard key={recording._id} recording={recording} onPlayed={handlePlayed} />
      ))}
    </div>
  )
}

export default SalesTrainingPage
